import { SalesEntryData, SalesEntryItem, PaymentEntryItem } from 'contexts/SalesEntryContext';
import { CreateSalesEntryPayload } from 'modules/transactions/new-sales-entry/services/use-sales-entry.service';

// Convert UI sales entry data into API payload
export const mapSalesEntryToAPI = (data: SalesEntryData): CreateSalesEntryPayload => {
  const exchangeData = data.exchangeSalesReturnData;

  const materialDiscount = data.items.reduce((sum, item) => sum + (item.materialDiscount || 0), 0);
  const diamondDiscount = data.items.reduce((sum, item) => sum + (item.diamondDiscount || 0), 0);
  const labDiscount = data.items.reduce((sum, item) => sum + (item.labDiscount || 0), 0);
  const mrpDiscount = data.items.reduce((sum, item) => sum + (item.mrpDiscount || 0), 0);

  // Sales order item list
  const salesOrderItemDetailsModelsList = data.items.map((item: SalesEntryItem) => ({
    mrp: item.mrp || 0,
    subMrp: item.mrp || 0,
    cgst: data.summary.cgst || 0,
    sgst: data.summary.sgst || 0,
    igst: 0,
    isSnmcc: item.isSNMCC || false,
    finalPrice: Math.round(Math.max(0, item.mrpPrice - (item.labDiscount + item.materialDiscount + item.diamondDiscount + item.mrpDiscount))),
    netPrice: Math.round(item.mrpPrice || 0),
    goldRate: item.todaysGoldRate || 0,
    diaPrice: item.diaMrp || 0,
    metalPrice: item.metalAmount || 0,
    materialDiscount: item.materialDiscount || 0,
    diamondDiscount: item.diamondDiscount || 0,
    labDiscount: item.labDiscount || 0,
    mrpDiscount: item.mrpDiscount || 0,
    productHeaderId: parseInt(String(item.id)) || 0,
    labMrp: item.makingCharges || 0,
    rewardDis: item.rewardDis || 0,
    deleted: false,
  }));

  // Totals
  const totalMrp = data.items.reduce((sum, item) => sum + (item.mrp || 0), 0);
  // const metalAmount = data.items.reduce((sum, item) => sum + ((item.netWt || 0) * 5800), 0);
  const metalAmount = data.items.reduce((sum, item) => {
    const amount = item?.metalAmount ?? 0;
    return sum + amount;
  }, 0);
  const otherCharges = data.items.reduce((sum, item) => {
    const stonePrice = item?.colorStonePrice ?? 0;
    return sum + stonePrice;
  }, 0);
  const labourAmount = data.items.reduce((sum, item) => {
    const making = item?.makingCharges ?? 0;
    return sum + making;
  }, 0);
  const totalOldGoldAmount = (data.oldGoldEntries || []).reduce((sum, entry) => sum + (entry.totalAmt || 0), 0);

  // Payment details
  const paymentDetailsModelList = (data.payment || []).map((payment: PaymentEntryItem) => ({
    paymentType: payment.method || 'Cash',
    amount: Math.round(payment.amount || 0),
    refNo: payment.referenceNumber || '',
  }));
  const totalRewardDiscount = data.items.reduce((sum, item) => sum + (item.totalRewardDiscount || 0), 0);


  const isIgstHan = data?.customer?.isIgst!

  // Main sales order payload
  const salesOrderDetailsModel = {
    salesOrderItemDetailsModelsList,
    metalAmount,
    labourAmount: labourAmount,
    otherCharges: otherCharges,
    discountAmount: data.summary.consumerOffer || 0,
    totalSoAmount: Math.round(data.summary.subAmt || totalMrp),
    finalSoAmount: Math.round(data.summary.totalAmt || 0),
    returnAmount: (data?.exchangeSalesReturnData?.returnSalesOrderModel?.totalReturnSaleOrderAmount || 0) + totalOldGoldAmount,
    finalDueAmount: data.summary.totalDueAmt || 0,
    materialDiscount: materialDiscount,
    diamondDiscount: diamondDiscount,
    labDiscount: labDiscount,
    mrpDiscount: mrpDiscount,
    customerId: parseInt(data.customer?.id || '0') || 0,
    cartId: Number(data?.draftId) ?? 0,
    BillingAddressId: data.customer?.BillingAddressId ? Number(data.customer?.BillingAddressId) : 0,
    ShippingAddressId: data?.customer?.ShippingAddressId ? Number(data.customer?.ShippingAddressId) : 0,
    salesPersonId: parseInt(data.salesPerson?.id || '0'),
    discountVoucherCodeId: parseInt(data.coupon?.id || '0') || 0,
    discountVoucherAmount: data.coupon?.discount || 0,
    storeDiscountOffer: data.summary.storeOffer || 0,
    cgst: data.summary.cgst || 0,
    sgst: data.summary.sgst || 0,
    subAmount: data.summary.subAmt || totalMrp,
    isOnGst: isIgstHan,
    vocAmount: data.voucherDetails?.voucherAmt || 0,
    customerMobileNo: data.customer?.phone || '',
    totalRewardDis: totalRewardDiscount
  };

  // Discount voucher
  const discountVoucherDetails = {
    discountVoucherCodeId: parseInt(data.coupon?.id || '0') || 0,
    discountVoucherAmount: data.coupon?.discount || 0,
  };

  // Reward redeem
  const rewardRedeemDetailsModelsList = data.rewardRedeemDetailsModelsList?.map(item => ({
    id: item.id || 0,
    materialTypeId: item.materialTypeId || 0,
    usedPoints: item.usedPoints || 0,
    amountConverted: item.amountConverted || 0,
    totalAmt: item.totalAmt || 0,
    rewardPercentage: item.rewardPercentage || 0,
  })) || [];
  // Old gold purchase order
  const purchaseOrderDetailsModel = (data.oldGoldEntries || []).length > 0
    ? {
      purchaseOrderItemsDetails: data.oldGoldEntries.map((entry) => ({
        karatId: parseInt(String(entry.kt)) || 0,
        rate: parseFloat(String(data.items?.[0].todaysGoldRate)) || data.items[0].todaysGoldRate,
        purity: parseFloat(String(entry.purity)) || 0,
        netWeight: parseFloat(String(entry.netWt)) || 0,
        equivalentWeight: parseFloat(String(entry.equivalentWt)) || 0,
        purchaseAmount: parseFloat(String(entry.totalAmt)) || 0,
        note: String(entry.note || ''),
      })),
      totalAmount: data.oldGoldEntries.reduce((sum, entry) => sum + (parseFloat(String(entry.totalAmt)) || 0), 0),
    }
    : undefined;

  // Return sales order from exchange
  const returnSalesOrderResponseModel = data.exchange && exchangeData
    ? {
      returnSalesOrderModel: {
        salesOrderId: exchangeData?.returnSalesOrderModel?.salesOrderId,
        returnSaleOrderDate: new Date().toISOString(),
        totalReturnSaleOrderAmount: exchangeData?.returnSalesOrderModel?.totalReturnSaleOrderAmount,
        totalQuantity: exchangeData?.returnSalesOrderModel?.totalQuantity,
        returnSaleType: exchangeData?.returnSalesOrderModel?.returnSaleType,
        customerId: exchangeData?.returnSalesOrderModel?.customerId,
        storeId: exchangeData?.returnSalesOrderModel?.storeId,
        salesPersonId: exchangeData?.returnSalesOrderModel?.salesPersonId,
        paymentType: 'Exchange',
        paymentReferenceNumber: '',
        description: 'Product exchange transaction',
        id: exchangeData?.returnSalesOrderModel?.id,
      },
      returnSalesOrderItems: exchangeData?.returnSalesOrderItems.map((item: any) => ({
        returnSalesOrderId: item.id,
        styleNo: item.styleNo,
        categoryName: item.categoryName,
        diamondWeight: item.diamondWeight,
        netWeight: item.netWeight,
        actualNetWeight: item.netWeight,
        grossWeight: item.grossWeight,
        metalPrice: item.metalPrice,
        laborPrice: item.laborPrice,
        diamondQuantity: item.diamondQuantity,
        quantity: 1,
        saleOrderAmount: item.saleOrderAmount,
        returnSaleOrderAmount: item.returnSaleOrderAmount,
        materialTypeId: item.materialTypeId,
        materialTypeName: item.materialTypeName,
        productHeaderId: item.productHeaderId,
        customerId: item.customerId,
        igiNo: item.igiNo,
        colorStonePrice: item.colorStonePrice,
        diamonMrp: item.diamonMrp,
        id: item.id,
        salesOrderItemId: item.id,
        refundRewardPoints: item?.refundRewardPoints ?? 0,
        rewardExpiryDays: item?.rewardExpiryDays ?? 0
      })),
    }
    : undefined;

  // Final API payload
  const payload: CreateSalesEntryPayload = {
    salesOrderDetailsModel,
    paymentDetailsModelList,
    discountVoucherDetails,
  };

  if (returnSalesOrderResponseModel) {
    payload.returnSalesOrderResponseModel = returnSalesOrderResponseModel;
  }

  if (rewardRedeemDetailsModelsList.length > 0) {
    payload.rewardRedeemDetailsModelsList = rewardRedeemDetailsModelsList;
  }
  if (purchaseOrderDetailsModel) {
    payload.purchaseOrderDetailsModel = purchaseOrderDetailsModel;
  }

  return payload;
};

// Validator utility
export const validateSalesEntryData = (data: SalesEntryData): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!data.items || data.items.length === 0) {
    errors.push('At least one item is required');
  }

  if (!data.customer || !data.customer.id) {
    errors.push('Customer is required');
  }

  if (!data.salesPerson || !data.salesPerson.id) {
    errors.push('Sales person is required');
  }

  // Only require payment method if dueAmount is NOT zero
  const dueAmount = Math.round((data.summary.totalDueAmt || 0) * 100);
  if ((!data.payment || data.payment.length === 0) && dueAmount > 1) {
    errors.push('At least one payment method is required');
  }

  if (data.payment && data.payment.length > 0) {
    const totalPayment = data.payment.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    if (totalPayment <= 0) {
      errors.push('Total payment amount must be greater than 0');
    }
  }

  const oldGoldAmount = (data.oldGoldEntries || []).reduce((sum, entry) => sum + (entry.totalAmt || 0), 0);
  const exchangeAmount = data?.exchangeSalesReturnData?.returnSalesOrderModel?.totalReturnSaleOrderAmount || 0;
  const redemptionAmount = data.redemption?.amount || 0;

  const paymentAmount =
    (data.payment || []).reduce((sum, payment) => sum + (payment.amount || 0), 0) +
    oldGoldAmount +
    exchangeAmount +
    redemptionAmount;

  const totalAmount = data.summary.totalAmt || 0;
  const amountDifference = Math.abs(paymentAmount - totalAmount);

  if (Math.round(data.summary.totalDueAmt * 100) > 1) {
    errors.push('Total Paid amount and Total Amount is not matching, Please check the payment details');
  }

  //  if (exchangeAmount < totalAmount) {
  //   errors.push('Exchange amount must be greater than or equal to the total amount');
  // }

  return {
    isValid: errors.length === 0,
    errors,
  };
};